import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

export default function HeroGeometry({ y }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-1, 1], [10, -10]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-1, 1], [-12, 12]), { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <motion.div
      style={{ y, perspective: 1000 }}
      className="relative w-full aspect-square max-w-[620px]"
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      data-testid="hero-geometry"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="absolute inset-0">
        <motion.div
          className="absolute inset-[14%] rounded-full overflow-hidden"
          initial={{ clipPath: "circle(0% at 50% 50%)" }}
          animate={{ clipPath: "circle(50% at 50% 50%)" }}
          transition={{ duration: 1.8, ease: [0.7, 0, 0.2, 1], delay: 0.5 }}
        >
          <img src="/media/branches.jpg" alt="Bare branches against a white sky" className="w-full h-full object-cover grayscale contrast-150 brightness-90 scale-110" />
          <div className="absolute inset-0 bg-[var(--ink)] mix-blend-multiply opacity-30" />
        </motion.div>
        <svg viewBox="-300 -300 600 600" className="absolute inset-0 w-full h-full overflow-visible" style={{ transform: "translateZ(60px)" }}>
          <motion.g initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }} transition={{ duration: 2.2, delay: 0.3 }} fill="none" stroke="var(--bone)" strokeWidth="1">
            <motion.circle r="290" strokeOpacity="0.35" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.4, ease: "easeInOut" }} />
            <motion.circle r="212" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.4, ease: "easeInOut", delay: 0.2 }} />
            <motion.path d="M-150 -130 L150 -130 L0 0 Z M0 0 L150 130 L-150 130 Z" strokeWidth="1.4" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.6, ease: "easeInOut", delay: 0.6 }} />
            <line x1="-300" y1="0" x2="300" y2="0" strokeOpacity="0.4" />
            <line x1="0" y1="-300" x2="0" y2="300" strokeOpacity="0.4" />
          </motion.g>
          <g className="spin-slow">
            {Array.from({ length: 72 }).map((_, i) => (
              <line key={i} x1="0" y1={-290} x2="0" y2={i % 6 === 0 ? -272 : -282} stroke="var(--bone)" strokeOpacity="0.6" transform={`rotate(${i * 5})`} />
            ))}
          </g>
          <circle r="5" fill="var(--signal)" />
          <text x="12" y="-12" fill="var(--ash)" fontSize="11" className="f-mono">(0, 0)</text>
          <text x="218" y="-8" fill="var(--ash)" fontSize="11" className="f-mono">r = 1</text>
          <text x="-292" y="-10" fill="var(--ash)" fontSize="11" className="f-mono">&phi; 1.618</text>
        </svg>
      </motion.div>
    </motion.div>
  );
}
